import { z } from 'zod';
import '@apexdesigner/schema-tools';
import { decimal, hasMany, options } from '@apexdesigner/schema-tools';

export const horse = z
  .object({
    id: z.number().hidden().describe('Unique identifier'),

    name: z
      .string()
      .meta({ format: 'upperCase' })
      .describe('Registered name, e.g. "PAX AMERICANA".'),

    sex: options([
      { value: 'Mare', name: 'Mare' },
      { value: 'Stallion', name: 'Stallion' },
    ]).describe('Mare or stallion.'),

    phenotype: z
      .string()
      .nullable()
      .optional()
      .describe('Conformation phenotype code, e.g. "E1", "IIP", "W".'),

    sireName: z
      .string()
      .nullable()
      .optional()
      .meta({ format: 'upperCase' })
      .describe('Name of the horse\'s sire, e.g. "WAR FRONT".'),

    owner: z
      .string()
      .nullable()
      .optional()
      .describe('Owner, e.g. "BREENA KAPLAN".'),

    yearOfBirth: z
      .number()
      .nullable()
      .optional()
      .excludeWhen("sex !== 'Mare'")
      .describe('Year the mare was foaled (legacy column `age`).'),

    bmeScore: z
      .number()
      .nullable()
      .optional()
      .displayName('BME Score')
      .excludeWhen("sex !== 'Mare'")
      .describe(
        "The mare's own BME (Biomechanical Efficiency) score as EQUIX recorded it, e.g. 97.9.",
      ),

    studFee: decimal()
      .nullable()
      .optional()
      .presentAs('currency')
      .excludeWhen("sex !== 'Stallion'")
      .describe("The stallion's advertised stud fee."),

    state: z
      .string()
      .nullable()
      .optional()
      .excludeWhen("sex !== 'Stallion'")
      .describe('State or province where the stallion stands, e.g. "KY".'),

    country: z
      .string()
      .nullable()
      .optional()
      .excludeWhen("sex !== 'Stallion'")
      .describe('Country where the stallion stands, e.g. "USA".'),

    active: z
      .boolean()
      .nullable()
      .optional()
      .describe(
        'Whether the horse is offered for matching. New horses start active; a\nhorse with matches is retired by clearing this rather than deleted.',
      ),

    height: z
      .number()
      .nullable()
      .optional()
      .describe('1. Height at the withers.'),

    heartGirth: z
      .number()
      .nullable()
      .optional()
      .describe('2. Heart girth (legacy column `hearth_girth_measure`).'),

    length: z.number().nullable().optional().describe('3. Length.'),

    flank: z.number().nullable().optional().describe('4. Flank.'),

    frontLeg: z.number().nullable().optional().describe('5. Front leg.'),

    backPastern: z.number().nullable().optional().describe('6. Back pastern.'),

    frontPastern: z
      .number()
      .nullable()
      .optional()
      .describe('7. Front pastern.'),

    back: z.number().nullable().optional().describe('8. Back.'),

    hipToHock: z.number().nullable().optional().describe('9. Hip to hock.'),

    hockToGround: z
      .number()
      .nullable()
      .optional()
      .describe('10. Hock to ground.'),

    ilium: z.number().nullable().optional().describe('11. Ilium.'),

    tuberCalcis: z
      .number()
      .nullable()
      .optional()
      .describe('12. Tuber calcis (legacy column `tuber_calcus_measure`).'),

    gaskin: z.number().nullable().optional().describe('13. Gaskin.'),

    tibia: z.number().nullable().optional().describe('14. Tibia.'),

    femur: z
      .number()
      .nullable()
      .optional()
      .describe('15. Femur (legacy column `femer_measure`).'),

    bodyLength: z.number().nullable().optional().describe('16. Body length.'),

    legacyId: z
      .number()
      .nullable()
      .optional()
      .disabled()
      .describe(
        'Id of the row in the legacy `mare` or `stallion` table, per sex.',
      ),

    groupMemberships: hasMany('HorseGroupMember'),

    matchesAsMare: hasMany('Match', 'mareId'),

    matchesAsStallion: hasMany('Match', 'stallionId'),

    groupMatches: hasMany('GroupMatch'),
  })
  .describe(
    'A mare or stallion with the 16 body measurements the OptiMatch calculation\nblends into a foal. Mares and stallions share one record; the sex decides\nwhich of the few sex-specific fields apply.',
  )
  .meta({ readAs: '{name}' })
  .as('Horse');

export { horse as horseSchema };
